import React from 'react';
import PropTypes from 'prop-types';
import { DateTime } from 'luxon';
import { Cushion, Grid, MaxWidth } from '@pitchero/react-ui';
import {
  TRAINING_AVAILABILITY_PROP_TYPES,
  TRAINING_SESSION_PROP_TYPES,
} from '../../lib/prop-types/training';
import CLUB_PROP_TYPES from '../../lib/prop-types/club';
import Availability from '../availability';
import HeaderBackgroundContainer from '../club-frame/header/background-container';
import SeoHead from '../seo';
import RosBottomBanner from '../adverts/ros-bottom-banner';
import TopBannerVideoPlayerSwitch from '../adverts/top-banner-video-player-switch';
import TrainingSessionLoadingState from './loading-state';
import Attendees from './attendees';
import Details from './details';
import Overview from './overview';

const TrainingSessionPage = ({
  availabilities,
  club,
  isLoading,
  isLoadingAvailability,
  onUpdateAvailability,
  session,
}) => {
  if (isLoading) {
    return <TrainingSessionLoadingState />;
  }

  if (!session) {
    // Handle the render before loading starts
    return null;
  }

  const startLuxon = DateTime.fromISO(session.start).setZone(club.timezone);
  const isInFuture = startLuxon > DateTime.local();

  const { advertisingTier, sportId } = club;

  return (
    <>
      <SeoHead
        club={club}
        description={session.description}
        route="calendar.training"
        routeParams={{ id: session.id }}
        title={`${session.groupName} - ${startLuxon.toFormat('cccc d MMMM yyyy, HH:mm')}`}
      />
      <HeaderBackgroundContainer
        imgSrc="https://images.pitchero.com/ui/1/1418128656_8024.jpg"
        preset="headline"
        height={club.includeAdverts ? 540 : 300}
      >
        <Cushion
          all="small"
          responsive={[{ minWidth: 'palm', props: { horizontal: 'medium' } }]}
          component="div"
        >
          <MaxWidth component="div" maxWidth={1366} center>
            <Cushion top="small" bottom="large">
              <TopBannerVideoPlayerSwitch
                advertisingTier={advertisingTier}
                pageType="calendar.training"
                sportId={sportId}
              />
            </Cushion>
            <Grid preset={isInFuture ? 'training' : 'trainingHistoric'}>
              {isInFuture && !session.isCancelled && (
                <div className="training-availability" style={{ gridArea: 'availability' }}>
                  <Availability
                    availabilities={availabilities}
                    club={club}
                    clubId={club.id}
                    teams={session.teams}
                    isLoading={isLoadingAvailability}
                    date={session.start}
                    onUpdateAvailability={onUpdateAvailability}
                  />
                </div>
              )}
              <Overview
                club={club}
                name={session.groupName}
                isCancelled={session.isCancelled}
                start={session.start}
                end={session.end}
              />
              <Details venue={session.venue} description={session.description} />
              <Attendees organiser={session.organiser} teams={session.teams} />
            </Grid>
            <Cushion vertical="medium">
              <RosBottomBanner />
            </Cushion>
          </MaxWidth>
        </Cushion>
      </HeaderBackgroundContainer>
    </>
  );
};

TrainingSessionPage.defaultProps = {
  session: null,
};

TrainingSessionPage.propTypes = {
  availabilities: PropTypes.arrayOf(TRAINING_AVAILABILITY_PROP_TYPES).isRequired,
  club: CLUB_PROP_TYPES.isRequired,
  isLoading: PropTypes.bool.isRequired,
  isLoadingAvailability: PropTypes.bool.isRequired,
  onUpdateAvailability: PropTypes.func.isRequired,
  session: TRAINING_SESSION_PROP_TYPES,
};

export default TrainingSessionPage;
